import {
  Component,
  OnDestroy,
  inject,
  Output,
  EventEmitter,
} from '@angular/core';
import { DrawerService } from '../../../../../../../core/services/drawer.service';
import {
  FequencyTimeSlots,
  FrequencyDrawerParams,
} from '../../../insights-data.interface';
import { DRAWER_IDS } from '../../../../../../../core/constants/drawer-ids.constant';
import { finalize, Observable, of, Subject, take, takeUntil } from 'rxjs';
import { NzTableModule } from 'ng-zorro-antd/table';
import { CommonModule } from '@angular/common';
import {
  FormGroup,
  FormBuilder,
  FormArray,
  ReactiveFormsModule,
} from '@angular/forms';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { InsightsDataService } from '../../../insights-data.service';
import { LoaderService } from '../../../../../../../core/services/loader.service';
import { CommonService } from '../../../../../../../shared/services/common.service';
import { ErrorResponse } from '../../../../../../../core/models/error.models';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { AllowOnlyValidNumbersDirective } from '../../../../../../../core/direactives/allowedOnlyValidNUmber.directive';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import _ from 'lodash';
import { TimeSlot } from '../../../../../../manage-settings/manage-settings.interface';
import { AuditContextService } from '../../../../../../audit-logs/audit-context.service';
import { EnterDataSaveNextUtility } from '../../enter-data-save-next.utility';
import { SetDurationComponent } from '../../set-duration/set-duration.component';
@Component({
  selector: 'app-frequency-interval',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzTableModule,
    NzInputModule,
    NzButtonModule,
    NgxMaskDirective,
    AllowOnlyValidNumbersDirective,
    NzSpinModule,
  ],
  providers: [provideNgxMask()],
  templateUrl: './frequency-interval.component.html',
  styleUrl: './frequency-interval.component.scss',
})
export class FrequencyIntervalComponent implements OnDestroy {
  private readonly auditContextService = inject(AuditContextService);
  private readonly destroy$ = new Subject<void>();
  drawerParmas: FrequencyDrawerParams = {} as FrequencyDrawerParams;
  loading$: Observable<boolean> = of(false);
  form!: FormGroup;
  categories = ['Duration', 'Interval Frequency'];
  @Output() saveAndNext = new EventEmitter<string>();
  constructor(
    private readonly drawerService: DrawerService,
    private readonly fb: FormBuilder,
    private readonly loaderService: LoaderService,
    private readonly insightsService: InsightsDataService,
    private readonly commonService: CommonService
  ) {
    this.loading$ = this.loaderService.loading$;
    this.form = this.fb.group({
      dataCategory: ['Interval Frequency'], // Default value for data category
      dataItems: this.fb.array([]), // FormArray for dynamic data items
    });
    this.setupDrawerSubscription();
  }

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<FrequencyDrawerParams>(DRAWER_IDS.FREQUENCY)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.drawerParmas = params;
          this.dataItems.clear();
          this.addDataItem();
        }
      });
  }

  private getNextWorkingDate(): string | null {
    if (!this.drawerParmas?.selectedDate) return null;
    return EnterDataSaveNextUtility.getNextWorkingDate(
      this.drawerParmas.selectedDate
    );
  }

  get hasNextDate(): boolean {
    return (
      this.drawerParmas.selectedUnit !== 1 && this.getNextWorkingDate() !== null
    );
  }

  get hasNextDateForUnit1(): boolean {
    return (
      this.drawerParmas.selectedUnit === 1 && this.getNextWorkingDate() !== null
    );
  }

  get nextDateHasFrequency(): boolean {
    return EnterDataSaveNextUtility.getNextDateInfo(
      this.drawerParmas.behaviorData,
      this.drawerParmas.selectedDate
    ).hasFrequency;
  }

  get nextDateHasDuration(): boolean {
    return EnterDataSaveNextUtility.getNextDateInfo(
      this.drawerParmas.behaviorData,
      this.drawerParmas.selectedDate
    ).hasDuration;
  }

  get nextDateHasNoData(): boolean {
    return EnterDataSaveNextUtility.getNextDateInfo(
      this.drawerParmas.behaviorData,
      this.drawerParmas.selectedDate
    ).hasNoData;
  }

  getTitle(startTime: string, endTime: string): string {
    return `${this.insightsService.formatTimeTextTo12Hour(startTime)} - ${this.insightsService.formatTimeTextTo12Hour(endTime)}`;
  }

  get isFormValid() {
    return this.dataItems.controls.some(
      control => control.get('unitValue')?.value.trim() !== ''
    );
  }

  get dataItems() {
    return this.form.get('dataItems') as FormArray;
  }
  get dataItemsControls(): FormGroup[] {
    return this.dataItems?.controls as FormGroup[];
  }
  // Method to add data items dynamically
  addDataItem() {
    // Find all records matching the selected date
    const matchedRecords = _.filter(
      this.drawerParmas.behaviorData.activityDataUnitMultiValues,
      { dataDate: this.drawerParmas.selectedDate, isDurationValue: false }
    );

    let timeSlots: TimeSlot[] | FequencyTimeSlots[] =
      this.drawerParmas.scheduleData.timeSlots;
    if (matchedRecords.length > 0) {
      timeSlots = this.insightsService.generateTimeSlots(matchedRecords);
    }
    if (timeSlots && timeSlots.length > 0) {
      for (const element of timeSlots) {
        // Find the matched record where both start and end times match
        const matchedRecord = matchedRecords.find(
          record =>
            record.startDataTime.startsWith(element.startTime) &&
            record.endDataTime.startsWith(element.endTime)
        );

        // Create a new FormGroup with either the matched value or an empty string
        const newDataItem = this.fb.group({
          id: [matchedRecord ? matchedRecord.id : 0],
          dataDate: [this.drawerParmas.selectedDate],
          startDataTime: [element.startTime],
          endDataTime: [element.endTime],
          unitValue: [matchedRecord ? matchedRecord.unitValue.toString() : ''], // Set matched unitValue or blank
        });

        this.dataItems.push(newDataItem);
      }
    }
  }

  onCancel() {
    this.drawerService.closeDrawer(DRAWER_IDS.FREQUENCY);
  }

  submitAndNext(): void {
    this.saveFrequencyInterval(() => {
      this.commonService.notify('success', 'Data saved successfully');
      const nextDate = this.getNextWorkingDate();
      if (nextDate) {
        this.saveAndNext.emit(nextDate);
      }
    });
  }

  continueWithDuration(): void {
    this.saveFrequencyInterval(() => {
      this.commonService.notify('success', 'Data saved successfully');
      const nextDate = this.getNextWorkingDate();
      if (nextDate) {
        this.openDurationDrawer(nextDate);
      }
    });
  }

  private saveFrequencyInterval(onSuccess: () => void): void {
    const payload = this.buildFrequencyIntervalPayload();
    this.loaderService.show();
    this.insightsService
      .saveMultiValue(payload)
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => this.loaderService.hide())
      )
      .subscribe({
        next: onSuccess,
        error: error => {
          this.loaderService.hide();
          this.handleError(error);
        },
      });
  }

  private buildFrequencyIntervalPayload(): {
    unitId: number;
    unitMultiValues: {
      id: number;
      dataDate: string;
      startDataTime: string;
      endDataTime: string;
      unitValue: string;
    }[];
    _modifiedFields: {
      field: string;
      fieldLabel: string;
      oldValue: string;
      newValue: string;
      isSensitive: boolean;
      meta: {
        date: string;
        startDataTime: string;
        endDataTime: string;
        selectedUnitName?: string;
      };
    }[];
  } {
    let filteredRecords = this.dataItems.value.map(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (item: { unitValue: string; [key: string]: any }) => ({ ...item })
    );
    filteredRecords = _.map(filteredRecords, item => {
      const formattedStartTime = `${item.startDataTime}:00`;
      const formattedEndTime = `${item.endDataTime}:00`;
      return _.assign({}, item, {
        startDataTime: formattedStartTime,
        endDataTime: formattedEndTime,
      });
    });

    const originalRecords =
      this.drawerParmas.behaviorData.activityDataUnitMultiValues.filter(
        (record: { dataDate: string; isDurationValue?: boolean }) =>
          record.dataDate === this.drawerParmas.selectedDate &&
          !record.isDurationValue
      );

    const modifiedFields = filteredRecords
      .map(
        (
          item: {
            id: number;
            startDataTime: string;
            endDataTime: string;
            unitValue: string;
          },
          idx: number
        ) => {
          const original = originalRecords[idx] || {};
          const oldValue = original.unitValue ?? '';
          const newValue = item.unitValue ?? '';
          if (oldValue !== newValue) {
            const unitName = this.drawerParmas.selectedUnitName
              ? ` | ${this.drawerParmas.selectedUnitName}`
              : '';
            return {
              field: 'unitValue',
              fieldLabel: `Frequency Interval${unitName} (${this.drawerParmas.selectedDate} ${item.startDataTime} - ${item.endDataTime})`,
              oldValue: this.normalizeCommaSeparatedValue(oldValue),
              newValue: this.normalizeCommaSeparatedValue(newValue),
              isSensitive: false,
              meta: {
                date: this.drawerParmas.selectedDate,
                startDataTime: item.startDataTime,
                endDataTime: item.endDataTime,
                selectedUnitName: this.drawerParmas.selectedUnitName,
              },
            };
          }
          return undefined;
        }
      )
      .filter(
        (
          f: unknown
        ): f is {
          field: string;
          fieldLabel: string;
          oldValue: string;
          newValue: string;
          isSensitive: boolean;
          meta: {
            date: string;
            startDataTime: string;
            endDataTime: string;
            selectedUnitName?: string;
          };
        } => f !== undefined
      );

    return {
      unitId: this.drawerParmas.unitId,
      unitMultiValues: filteredRecords,
      _modifiedFields: modifiedFields,
    };
  }

  private openDurationDrawer(nextDate: string): void {
    const updatedParams: FrequencyDrawerParams = {
      ...this.drawerParmas,
      selectedDate: nextDate,
      selectedDay: EnterDataSaveNextUtility.getDayName(nextDate),
      selectedRow: {
        id: 0,
        date: nextDate,
        day: EnterDataSaveNextUtility.getDayName(nextDate),
        individualIntervalDetails: '',
        totalFrequency: '',
        totalDuration: '',
        averageDuration: '',
        action: '',
        isFrequencyValue: false,
        isDuration: false,
        isUpdateTotal: false,
        isFrequencyOnly:
          this.drawerParmas.behaviorData?.activityDataUnitValues?.some(
            record => record.dataDate === nextDate
          ) ?? false,
        commentCount: 0,
      },
    };

    this.drawerService.closeDrawer(DRAWER_IDS.FREQUENCY);
    this.drawerService.openDrawer(
      SetDurationComponent,
      this.drawerParmas.durationDrawerTitleTemplate ?? 'Data',
      1170,
      true,
      undefined,
      updatedParams,
      DRAWER_IDS.DURATION
    );
  }

  submitForm() {
    this.saveFrequencyInterval(() => {
      this.commonService.notify('success', `Data saved successfully`);
      this.drawerParmas.onDataSaved?.();
      this.onCancel();
    });
  }
  private handleError({ errors, message }: ErrorResponse): void {
    const errorMessages = errors.map(
      err => err.description || err.detailedMessage
    );
    this.commonService.notify('error', errorMessages, message);
  }

  private normalizeCommaSeparatedValue(value: string): string {
    return value
      .split(',')
      .map(item => item.trim())
      .filter(item => item !== '')
      .join(',');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
